import { AfterViewInit, Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router } from '@angular/router';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SideBarMenus } from '@core/interface/common/router-module';
import { MI, MIParent } from '@core/constant/menu-item-settings';
import { PermissionService } from '@core/services/permission.service';
import { Store } from '@ngrx/store';
import { selectPermissions } from '@core/store/auth/auth.selector';
import { hasAccessToMethod, hasAccessToObject } from '@core/utils/permission';

@Component({
  selector: 'app-slidebar',
  standalone: true,
  imports: [CommonModule, MatTreeModule, MatButtonModule, MatIconModule, RouterModule,TranslateModule],
  templateUrl: './slidebar.component.html',
  styleUrl: './slidebar.component.scss',
})
export class SlidebarComponent implements OnInit, AfterViewInit {
  treeControl = new NestedTreeControl<SideBarMenus>(
    (node) => node.children ?? []
  );

  dataSource : SideBarMenus[] = [];
  permissions: string[] = [];

  constructor(
    private location: Location, 
    private translate: TranslateService, 
    private permissionService: PermissionService,
    private store: Store,
    private router: Router
  ) {
  }
  ngOnInit(): void {
    this.store.select(selectPermissions).subscribe((permissions: string[]) => {
      this.permissions = permissions;
      this.initDataSource();
    });

    this.initDataSource();
    this.translate.onLangChange.subscribe(() => {
      this.initDataSource();
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const currentPath = this.location.path().split('?')[0];
      this.openNodeByUrl(currentPath || '/auth-clients');
    });
  }

  initDataSource(): void {
    const items: SideBarMenus[] = [];
    
    let permissions: string[] = [];
    if (this.permissions && this.permissions.length > 0) {
      permissions = this.permissions;
    } else {
      permissions = this.permissionService.getPermissions();
    }

    if (hasAccessToObject(permissions, 'BusinessClient', 'CanGet')) {
      items.push(
        MI(
          this.translate.instant('SIDEBAR.BUSINESS_CLIENTS'),
          'business',
          '/business-clients'
        )
      );
    }

    if (hasAccessToObject(permissions, 'SubscriptionType', 'CanGet')) {
      items.push(
        MI(
          this.translate.instant('SIDEBAR.SUBSCRIPTION_TYPE'),
          'subscriptions',
          '/subscription-type-list'
        )
      );
    }

    if (
      hasAccessToObject(permissions, 'SubscriptionPlan', 'CanGet') ||
      hasAccessToMethod(permissions, 'SubscriptionPlan', 'GetSystemAdmin') ||
      hasAccessToMethod(permissions, 'SubscriptionPlan', 'GetClientAdmin')
    ) {
      items.push(
        MI(
          this.translate.instant('SIDEBAR.SUBSCRIPTION_PLAN'),
          'card_membership',
          '/subscription-plans'
        )
      );
    }

    if (hasAccessToObject(permissions, 'Subscription', 'CanGet')) {
      items.push(
        MI(
          this.translate.instant('SIDEBAR.SUBSCRIPTIONS'),
          'subscriptions',
          '/subscriptions'
        )
      );
    }
    

    items.push(
      MI(
        this.translate.instant('SIDEBAR.CONTRACTS'),
        'description',
        '/contracts'
      )
    );
    if (hasAccessToObject(permissions, 'Payment', 'CanGet')) {
      items.push(
        MI(
          this.translate.instant('SIDEBAR.PAYMENTS'),
          'payments',
          '/payments'
        )
      );
    }
    const systemAdminChildren: SideBarMenus[] = [];

    if (hasAccessToObject(permissions, 'AuthClient', 'CanGet')) {
      systemAdminChildren.push(
        MI(
          this.translate.instant('SIDEBAR.AUTH_CLIENTS'),
          'tv_signin',
          '/auth-clients'
        )
      );
    }

    if (hasAccessToObject(permissions, 'UsrUser', 'CanGet')) {
      systemAdminChildren.push(
        MI(
          this.translate.instant('SIDEBAR.USERS'),
          'people',
          '/users'
        )
      );
    }

    if (hasAccessToObject(permissions, 'UsrRole', 'CanGet')) {
      systemAdminChildren.push(
        MI(
          this.translate.instant('SIDEBAR.ROLES'),
          'admin_panel_settings',
          '/roles'
        )
      );
    }

    if (hasAccessToObject(permissions, 'Profile', 'CanGet')) {
      systemAdminChildren.push(
        MI(
          this.translate.instant('SIDEBAR.PROFILES'),
          'badge',
          '/profiles'
        )
      );
    }

    if (hasAccessToObject(permissions, 'GenObject', 'CanGet')) {
      systemAdminChildren.push(
        MI(
          this.translate.instant('SIDEBAR.GEN_OBJECTS'),
          'category',
          '/gen-objects'
        )
      );
    }

    if (systemAdminChildren.length > 0) {
      items.push(
        MIParent(
          this.translate.instant('SIDEBAR.SYSTEM_ADMINISTRATION'),
          'settings',
          systemAdminChildren
        )
      );
    }

    this.dataSource = items;
  }

  isActive(url: string): boolean {
    const currentUrl = this.location.path().split('?')[0];
    return currentUrl === url;
  }

  hasChild = (_: number, node: SideBarMenus) =>
    !!node.children && node.children.length > 0;

  findNodeByUrl(
    node: SideBarMenus,
    url: string,
    arrIndex: number[]
  ):number[] | null {
    if (node?.link === url) {
      return arrIndex;
    }
    if (node.children) {
      let i = 0;
      for (const child of node.children) {
        const arrIndexClone = [...arrIndex, i++];
        const found = this.findNodeByUrl(child, url, arrIndexClone);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  expandNode(node: SideBarMenus, arrIndex: number[]): void {
    this.treeControl.expand(node);
    if (node.children && arrIndex.length) {
      const index = arrIndex.shift()!;
      this.expandNode(node.children[index], arrIndex);
    }
  }

  openNodeByUrl(url: string): void {
    let arrIndex: number[] = [];
    const nodeToOpen = this.dataSource.find((node) => {
      const result = this.findNodeByUrl(node, url, []);
      if (result) {
        arrIndex = result;
        return true;
      }
      return false;
    });
    if (nodeToOpen) {
      this.expandNode(nodeToOpen, arrIndex);
    }
  }

  navigateToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}
